import { useEffect, useMemo, useState } from 'react'
import Select, { components } from 'react-select'
import styled from 'styled-components'
import Icon from '@/components/common/Icon'
import BaseCheckbox from '@/components/common/BaseCheckbox'
import classNames from 'classnames'
import CreativesBaseModal from '@/components/Creatives/common/CreativesBaseModal'
import CreativesSelectMultiplyMobile from '@/components/Creatives/mobile/CreativesSelectMultiplyMobile'
import { hideTablet } from '@/components/Creatives/CreativesStyled'
import useWindowSize from '@/hooks/useWindowSize'
import { useRouter } from 'next/router'
import Text from 'components/common/Text'
import { useTranslations } from 'next-intl'

const CreativeCheckbox = styled(BaseCheckbox)`
  padding: 0;
  font-family: sans-serif;

  span {
    border: 2px solid #c9cbce;
    width: 24px;
    height: 24px;
  }

  div {
    color: black;
    line-height: 16px;
    font-size: 14px;
    font-weight: 500;
  }
`

const CreativeCheckboxMobile = styled(BaseCheckbox)`
  padding: 8px;
`

const ButtonsDropdownWrapper = styled.div`
  position: relative;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e0e1e2;
`

const DropdownButton = styled.button`
  color: var(--purple);
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px;
  letter-spacing: -0.21px;
  background-color: transparent;
  padding: 16px 8px;
  font-family: sans-serif;
`

const SelectWrap = styled.div`
  .creative-multi-select-modal {
    ${hideTablet};
  }
`

const InputOption = ({
  getStyles,
  isSelected,
  innerProps,
  label,
  data,

  ...rest
}) => {
  const props = {
    ...innerProps,
  }

  return (
    <components.Option {...rest} getStyles={getStyles} innerProps={props}>
      <div>
        <CreativeCheckbox
          value={data.value}
          label={label}
          checked={isSelected}
        />
      </div>
    </components.Option>
  )
}

const Menu = (props) => {
  const t = useTranslations()

  return (
    <>
      <components.Menu {...props}>
        <ButtonsDropdownWrapper>
          <DropdownButton
            onClick={() => {
              props.setValue(props.options)
            }}
          >
            {t('favorites.selectAll', { count: props.options.length })}
          </DropdownButton>
          <DropdownButton
            onClick={() => {
              props.setValue(null)
              props.selectProps.onInputChange('', {
                action: 'input-change',
              })
            }}
          >
            {t('creatives.reset')}
          </DropdownButton>
        </ButtonsDropdownWrapper>
        <div>
          <div>{props.children}</div>
        </div>
      </components.Menu>
    </>
  )
}

const MultiValueLabel = (props) => {
  return (
    <div>
      <components.MultiValueRemove {...props}>
        <Text color="white">{props.data.label}</Text>
      </components.MultiValueRemove>
    </div>
  )
}

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen && (
        <Icon
          onMouseDown={() =>
            props.selectProps.onInputChange('', {
              action: 'input-change',
            })
          }
          icon="x-close"
          color="grey"
        />
      )}
      {!props.selectProps.menuIsOpen && (
        <Icon icon="chevron-down" color="grey" />
      )}
    </components.DropdownIndicator>
  )
}
const CreativeMultiSelect = ({
  list,
  onChange,
  initialSelect,
  controlShouldRenderValue = false,
}) => {
  const [isMounted, setIsMounted] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState([])
  const [input, setInput] = useState('')
  const [open, setOpen] = useState(false)
  const { locale } = useRouter()
  const t = useTranslations('creatives')
  const { isMobile } = useWindowSize()

  const handleChange = (selected) => {
    setSelectedOptions(selected)
  }

  const isSelected = (value) => {
    if (!selectedOptions) return

    return selectedOptions.find((selected) => selected.value === value)
  }

  const onChangeSelected = (isSelected, data) => {
    setSelectedOptions((prevState) => {
      if (isSelected) {
        return [...prevState, data]
      }

      return prevState.filter((item) => item.value !== data.value)
    })
  }

  const getPlaceholder = useMemo(
    () => {
      return selectedOptions?.length
        ? t('creatives.chooseItems', { count: selectedOptions.length })
        : t('creatives.enterValue')
    },
    [selectedOptions],
    locale
  )

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (isMounted) {
      onChange({
        select: selectedOptions?.map((select) => select.value) ?? [],
      })
    }
  }, [selectedOptions])

  useEffect(() => {
    if (!initialSelect) return
    let transformedSelect = [...initialSelect]
    if (!transformedSelect.length) return

    if (typeof transformedSelect[0] === 'object') {
      transformedSelect = transformedSelect.map((item) => item.value)
    }
    if (transformedSelect.length !== selectedOptions?.length) {
      setSelectedOptions(transformedSelect.map((item) => ({ value: item })))
    }
  }, [initialSelect])

  const colourStyles = {
    option: (base) => {
      return {
        ...base,
        padding: '12px 0 12px 8px',
        position: 'relative',
        'z-index': 9999,
        color: 'black',
        backgroundColor: 'white',
        ':hover': {
          backgroundColor: '#e0e1e2',
        },
      }
    },
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 999,
    }),
    container: (base) => ({
      ...base,
      'z-index': 100,
    }),
    menu: (provide) => ({
      ...provide,
      padding: '4px 12px 12px',
      borderRadius: '12px',
      'z-index': 100,
      display: 'none',
      '@media (min-width: 768px)': {
        display: 'block',
      },
    }),
    menuList: (provide) => ({
      ...provide,
      padding: 0,
    }),
    indicatorSeparator: (defaultStyles) => ({
      ...defaultStyles,
      display: 'none',
    }),
    indicatorsContainer: (defaultStyles) => ({
      ...defaultStyles,
      'padding-right': '16px',
    }),
    placeholder: (defaultStyles) => ({
      ...defaultStyles,
      color: '#E0E1E2',
    }),
    control: (baseStyles) => ({
      ...baseStyles,
      borderColor: 'var(--divider)',
      boxShadow: 'none',
      '&:hover': {
        border: 'none',
      },
      color: 'red',
      borderRadius: '12px',
      minHeight: '44px',
      borderWidth: 0,
      outline: 'thick',
      outlineColor: 'var(--divider)',
      outlineWidth: '1px',
      outlineStyle: 'solid',
      ':hover': {
        boxShadow: '0px 0px 0px 2px #d4dced',
        borderColor: '#d4dced',
      },
      'padding-left': '12px',
    }),
    multiValue: (styles) => {
      return {
        ...styles,
        backgroundColor: 'var(--purple)',
        borderRadius: '12px',
      }
    },
    multiValueLabel: (styles) => ({
      ...styles,
      color: 'white',
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: 'white',
      ':hover': {
        color: 'var(--red)',
      },
    }),
  }

  const multiSelectClasses = classNames({
    'multi-select': true,
    open: open,
  })

  const onCloseMenuMobile = () => {
    setOpen(false)
  }

  const onCloseMenu = () => {
    if (!isMobile) {
      setOpen(false)
    }
  }

  const updatedSelect = useMemo(() => {
    return !isMobile || !locale ? 0 : selectedOptions
  }, [selectedOptions, isMobile, locale])

  const isVisibleMultiSelectMobile = useMemo(() => {
    return isMobile && open
  }, [open])

  return (
    <SelectWrap>
      <Select
        key={updatedSelect}
        className={multiSelectClasses}
        defaultValue={selectedOptions}
        isMulti
        allowSelectAll={true}
        menuIsOpen={open}
        onMenuOpen={() => setOpen(true)}
        onMenuClose={onCloseMenu}
        placeholder={getPlaceholder}
        styles={colourStyles}
        defaultMenuIsOpen={true}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        menuShouldScrollIntoView={true}
        menuShouldBlockScroll={open}
        isClearable={false}
        onChange={handleChange}
        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        inputValue={input}
        menuPlacement="bottom"
        minMenuHeight={366}
        onInputChange={(value, action) => {
          if (action.action === 'input-change') setInput(value)
        }}
        options={list}
        controlShouldRenderValue={controlShouldRenderValue}
        components={{
          Menu,
          Option: (props) => (
            <InputOption {...props} handleChange={handleChange} />
          ),
          DropdownIndicator,
          MultiValueLabel,
        }}
      />
      {isVisibleMultiSelectMobile && (
        <CreativesBaseModal
          className="creative-multi-select-modal"
          Component={CreativesSelectMultiplyMobile}
          isShowScrollAfterDestroy={false}
          onClose={onCloseMenuMobile}
          inputValue={input}
          onChange={setInput}
        >
          {list.map((item) => (
            <div key={item.value}>
              <CreativeCheckboxMobile
                value={item.value}
                label={item.label}
                checked={isSelected(item.value)}
                onChange={(selected) => onChangeSelected(selected, item)}
              />
            </div>
          ))}
        </CreativesBaseModal>
      )}
    </SelectWrap>
  )
}

export default CreativeMultiSelect
